/**
 * Module dependencies.
 */

import { ErrorMessage, FormControl, FormGroup, Label } from 'src/components/core/forms/styles';
import { FormInputProps } from 'src/types/form';
import { HTMLProps, forwardRef, useCallback, useRef } from 'react';
import { Svg } from 'src/components/core/svg';
import { assignRefs } from 'src/core/utils/refs';
import { formatDate } from 'src/core/utils/date';
import calendarSvg from 'src/assets/svgs/calendar-xs.svg';
import styled from 'styled-components';

/**
 * Export `InputDateProps` interface.
 */

export type InputDateProps = HTMLProps<HTMLInputElement> & FormInputProps<HTMLInputElement>;

/**
 * `InputGroup` styled component.
 */

const InputGroup = styled.div`
  --input-date-left: 15px;
  --input-date-icon-size: var(--space-sm);
  --input-date-padding: 6px var(--input-date-left) 6px
    calc(var(--input-date-left) + var(--input-date-icon-size) + var(--space-xxs));

  position: relative;
`;

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled(FormControl)`
  color: transparent;
  cursor: pointer;
  padding: var(--input-date-padding);
  position: relative;

  ::-webkit-calendar-picker-indicator {
    bottom: 0;
    left: 0;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
  }
`;

/**
 * `Value` styled component.
 */

const Value = styled(FormControl).attrs({ as: 'span' })<{ disabled?: boolean }>`
  bottom: 0;
  left: 0;
  padding: var(--input-date-padding);
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

/**
 * `CalendarIcon` styled component.
 */

const CalendarIcon = styled(Svg).attrs({
  icon: calendarSvg,
  size: 'var(--input-date-icon-size)'
})<{ disabled?: boolean }>`
  color: var(--color-slate800);
  left: var(--input-date-left);
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: color var(--transition-default);
  z-index: 2;

  :disabled {
    color: var(--color-slate500);
    pointer-events: none;
  }
`;

/**
 * Export `InputDate` component.
 */

export const InputDate = forwardRef((props: Omit<InputDateProps, 'isOptional' | 'type'>, ref: any) => {
  const { className, disabled, error, hasError, id, label, placeholder, value, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const handleShowPicker = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.showPicker();
    }
  }, []);

  return (
    <FormGroup className={className}>
      <Label disabled={disabled} htmlFor={id}>
        {label}
      </Label>

      <InputGroup onClick={handleShowPicker}>
        <CalendarIcon disabled={disabled} />

        <StyledInput
          data-error={hasError}
          disabled={disabled}
          id={id}
          ref={assignRefs(ref, inputRef)}
          type={'date'}
          value={value}
          {...rest}
        />

        <Value>{value ? formatDate(value as string) : placeholder}</Value>
      </InputGroup>

      {hasError && <ErrorMessage disabled={disabled}>{error}</ErrorMessage>}
    </FormGroup>
  );
});

/**
 * `InputDate` display name.
 */

InputDate.displayName = 'InputDate';
